/**
 * ScenarioEditor Component
 * Rich editor for creating and editing test scenarios
 */

import React, { useState, useEffect } from 'react';
import {
  apiClient,
  TestScenario,
  TestScenarioCreate,
  TestScenarioUpdate,
} from '../services/api';
import './ScenarioEditor.css';

interface ScenarioEditorProps {
  folderId: string;
  scenarioId?: string;
  onSave?: (scenario: TestScenario) => void;
  onCancel?: () => void;
}

interface TestCaseFormData {
  test_case_description: string;
  preconditions: string;
  expected_result: string;
}

interface EvidenceFile {
  file: File;
  environment: 'Stage' | 'Preprod' | 'PROD';
}

const PRIORITY_OPTIONS: Array<'Critical' | 'High' | 'Medium' | 'Low'> = [
  'Critical',
  'High',
  'Medium',
  'Low',
];

const ENVIRONMENT_OPTIONS: Array<'Stage' | 'Preprod' | 'PROD'> = [
  'Stage',
  'Preprod',
  'PROD',
];

export const ScenarioEditor: React.FC<ScenarioEditorProps> = ({
  folderId,
  scenarioId,
  onSave,
  onCancel,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState<TestScenarioCreate | TestScenarioUpdate>({
    folder_id: folderId,
    scenario_description: '',
    expected_result: '',
    priority: 'Medium',
    tags: [],
  });
  const [tagInput, setTagInput] = useState('');
  const [evidenceFiles, setEvidenceFiles] = useState<EvidenceFile[]>([]);
  const [existingEvidence, setExistingEvidence] = useState<string[]>([]);
  
  // Test case creation state
  const [createTestCases, setCreateTestCases] = useState(false);
  const [testCases, setTestCases] = useState<TestCaseFormData[]>([
    { test_case_description: '', preconditions: '', expected_result: '' }
  ]);

  const loadScenario = async () => {
    if (!scenarioId) return;

    setIsLoading(true);
    setError(null);
    try {
      const scenario = await apiClient.getScenario(scenarioId);
      setFormData({
        scenario_description: scenario.scenario_description,
        expected_result: scenario.expected_result || '',
        priority: scenario.priority,
        tags: scenario.tags,
      });
      setExistingEvidence(scenario.expected_evidence || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load scenario');
    } finally {
      setIsLoading(false);
    }
  };

  // Load existing scenario if editing
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (scenarioId) {
      loadScenario();
    }
  }, [scenarioId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.scenario_description.trim()) {
      setError('Scenario description is required');
      return;
    }

    // Validate test cases if enabled
    if (createTestCases) {
      for (let i = 0; i < testCases.length; i++) {
        if (!testCases[i].test_case_description.trim()) {
          setError(`Test case ${i + 1}: Description is required`);
          return;
        }
        if (!testCases[i].expected_result.trim()) {
          setError(`Test case ${i + 1}: Expected result is required`);
          return;
        }
      }
    }

    setIsLoading(true);
    setError(null);

    try {
      let savedScenario: TestScenario;

      // Prepare data - convert empty strings to undefined for optional fields
      const submitData = {
        ...formData,
        expected_result: formData.expected_result?.trim() || undefined
      };

      if (scenarioId) {
        // Update existing scenario
        savedScenario = await apiClient.updateScenario(scenarioId, submitData as TestScenarioUpdate);
      } else {
        // Create new scenario
        savedScenario = await apiClient.createScenario(submitData as TestScenarioCreate);
      }

      // Upload evidence files if any
      if (evidenceFiles.length > 0) {
        for (let i = 0; i < evidenceFiles.length; i++) {
          await apiClient.uploadScenarioEvidence(
            savedScenario.id,
            evidenceFiles[i].file,
            existingEvidence.length + i
          );
        }
      }

      // Create test cases if enabled
      if (createTestCases && !scenarioId) {
        for (const testCase of testCases) {
          if (testCase.test_case_description.trim()) {
            await apiClient.createTestCaseManually({
              folder_id: folderId,
              scenario_id: savedScenario.id,
              scenario_version: savedScenario.version,
              test_case_description: testCase.test_case_description,
              preconditions: testCase.preconditions || undefined,
              expected_result: testCase.expected_result,
              priority: 'Medium',
              tags: [],
            });
          }
        }
      }

      onSave?.(savedScenario);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save scenario');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddTag = () => {
    const tag = tagInput.trim();
    if (tag && !formData.tags.includes(tag)) {
      setFormData({
        ...formData,
        tags: [...formData.tags, tag],
      });
      setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setFormData({
      ...formData,
      tags: formData.tags.filter((tag) => tag !== tagToRemove),
    });
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const files = Array.from(e.target.files);
      const newEvidenceFiles = files.map(file => ({
        file,
        environment: 'Stage' as 'Stage' | 'Preprod' | 'PROD'
      }));
      setEvidenceFiles([...evidenceFiles, ...newEvidenceFiles]);
    }
  };

  const handleRemoveFile = (index: number) => {
    setEvidenceFiles(evidenceFiles.filter((_, i) => i !== index));
  };

  const handleEnvironmentChange = (index: number, environment: 'Stage' | 'Preprod' | 'PROD') => {
    const updated = [...evidenceFiles];
    updated[index].environment = environment;
    setEvidenceFiles(updated);
  };

  // Test case management functions
  const handleAddTestCase = () => {
    setTestCases([
      ...testCases,
      { test_case_description: '', preconditions: '', expected_result: '' }
    ]);
  };

  const handleRemoveTestCase = (index: number) => {
    if (testCases.length > 1) {
      setTestCases(testCases.filter((_, i) => i !== index));
    }
  };

  const handleTestCaseChange = (index: number, field: keyof TestCaseFormData, value: string) => {
    const updated = [...testCases];
    updated[index][field] = value;
    setTestCases(updated);
  };

  return (
    <div className="scenario-editor">
      <h2>{scenarioId ? 'Edit Test Scenario' : 'Create Test Scenario'}</h2>

      {error && (
        <div className="error-message" role="alert">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        {/* Scenario Description */}
        <div className="form-group">
          <label htmlFor="scenario-description">
            Scenario Description *
          </label>
          <textarea
            id="scenario-description"
            value={formData.scenario_description}
            onChange={(e) =>
              setFormData({ ...formData, scenario_description: e.target.value })
            }
            placeholder="Describe the test scenario in detail..."
            disabled={isLoading}
            required
            rows={6}
          />
        </div>

        {/* Expected Result */}
        <div className="form-group">
          <label htmlFor="expected-result">Expected Result</label>
          <textarea
            id="expected-result"
            value={formData.expected_result}
            onChange={(e) =>
              setFormData({ ...formData, expected_result: e.target.value })
            }
            placeholder="Describe the expected result..."
            disabled={isLoading}
            rows={4}
          />
        </div>

        {/* Priority */}
        <div className="form-group">
          <label htmlFor="priority">Priority *</label>
          <select
            id="priority"
            value={formData.priority}
            onChange={(e) =>
              setFormData({
                ...formData,
                priority: e.target.value as 'Critical' | 'High' | 'Medium' | 'Low',
              })
            }
            disabled={isLoading}
            required
          >
            {PRIORITY_OPTIONS.map((priority) => (
              <option key={priority} value={priority}>
                {priority}
              </option>
            ))}
          </select>
        </div>

        {/* Tags */}
        <div className="form-group">
          <label htmlFor="tag-input">Tags</label>
          <div className="tag-input-container">
            <input
              id="tag-input"
              type="text"
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleAddTag();
                }
              }}
              placeholder="Add a tag and press Enter"
              disabled={isLoading}
            />
            <button
              type="button"
              className="btn btn-secondary"
              onClick={handleAddTag}
              disabled={isLoading || !tagInput.trim()}
            >
              Add Tag
            </button>
          </div>
          {formData.tags.length > 0 && (
            <div className="tag-list">
              {formData.tags.map((tag) => (
                <span key={tag} className="tag">
                  {tag}
                  <button
                    type="button"
                    className="tag-remove"
                    onClick={() => handleRemoveTag(tag)}
                    disabled={isLoading}
                    aria-label={`Remove tag ${tag}`}
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Expected Evidence Upload */}
        <div className="form-group">
          <label htmlFor="evidence-upload">Expected Evidence (Screenshots)</label>
          <input
            id="evidence-upload"
            type="file"
            accept="image/*"
            multiple
            onChange={handleFileSelect}
            disabled={isLoading}
          />
          <p className="form-help">
            Upload screenshots showing the expected behavior
          </p>

          {/* Existing Evidence */}
          {existingEvidence.length > 0 && (
            <div className="evidence-preview">
              <h4>Existing Evidence:</h4>
              <div className="evidence-list">
                {existingEvidence.map((path, index) => (
                  <div key={index} className="evidence-item">
                    <img
                      src={`${process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000'}${path}`}
                      alt={`Evidence ${index + 1}`}
                    />
                    <span>{path.split('/').pop()}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* New Evidence Files */}
          {evidenceFiles.length > 0 && (
            <div className="evidence-preview">
              <h4>New Evidence to Upload:</h4>
              <div className="evidence-list">
                {evidenceFiles.map((evidenceFile, index) => (
                  <div key={index} className="evidence-item">
                    <img
                      src={URL.createObjectURL(evidenceFile.file)}
                      alt={evidenceFile.file.name}
                    />
                    <div className="evidence-details">
                      <span className="evidence-filename">{evidenceFile.file.name}</span>
                      <select
                        className="environment-select"
                        value={evidenceFile.environment}
                        onChange={(e) => handleEnvironmentChange(index, e.target.value as 'Stage' | 'Preprod' | 'PROD')}
                        disabled={isLoading}
                      >
                        {ENVIRONMENT_OPTIONS.map(env => (
                          <option key={env} value={env}>{env}</option>
                        ))}
                      </select>
                    </div>
                    <button
                      type="button"
                      className="btn btn-icon btn-danger"
                      onClick={() => handleRemoveFile(index)}
                      disabled={isLoading}
                      aria-label={`Remove ${evidenceFile.file.name}`}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Test Case Creation Section (Only for new scenarios) */}
        {!scenarioId && (
          <div className="test-case-section">
            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={createTestCases}
                  onChange={(e) => setCreateTestCases(e.target.checked)}
                  disabled={isLoading}
                />
                <span>Create test cases with this scenario (optional)</span>
              </label>
              <p className="form-help">
                Enable this to create one or more test cases linked to this scenario
              </p>
            </div>

            {createTestCases && (
              <div className="test-cases-container">
                <h3>Test Cases</h3>
                <p className="section-description">
                  Create test cases that will be linked to this scenario. You can add multiple test cases.
                </p>

                {testCases.map((testCase, index) => (
                  <div key={index} className="test-case-form">
                    <div className="test-case-header">
                      <h4>Test Case {index + 1}</h4>
                      {testCases.length > 1 && (
                        <button
                          type="button"
                          className="btn btn-sm btn-danger"
                          onClick={() => handleRemoveTestCase(index)}
                          disabled={isLoading}
                        >
                          Remove
                        </button>
                      )}
                    </div>

                    <div className="form-group">
                      <label htmlFor={`tc-description-${index}`}>
                        Test Case Description *
                      </label>
                      <textarea
                        id={`tc-description-${index}`}
                        value={testCase.test_case_description}
                        onChange={(e) =>
                          handleTestCaseChange(index, 'test_case_description', e.target.value)
                        }
                        placeholder="Describe the specific test case..."
                        disabled={isLoading}
                        required={createTestCases}
                        rows={4}
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor={`tc-preconditions-${index}`}>
                        Preconditions
                      </label>
                      <textarea
                        id={`tc-preconditions-${index}`}
                        value={testCase.preconditions}
                        onChange={(e) =>
                          handleTestCaseChange(index, 'preconditions', e.target.value)
                        }
                        placeholder="Enter any preconditions for this test case..."
                        disabled={isLoading}
                        rows={3}
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor={`tc-expected-${index}`}>
                        Expected Result *
                      </label>
                      <textarea
                        id={`tc-expected-${index}`}
                        value={testCase.expected_result}
                        onChange={(e) =>
                          handleTestCaseChange(index, 'expected_result', e.target.value)
                        }
                        placeholder="Describe the expected result for this test case..."
                        disabled={isLoading}
                        required={createTestCases}
                        rows={3}
                      />
                    </div>
                  </div>
                ))}

                <button
                  type="button"
                  className="btn btn-secondary add-test-case-btn"
                  onClick={handleAddTestCase}
                  disabled={isLoading}
                >
                  + Add Another Test Case
                </button>
              </div>
            )}
          </div>
        )}

        {/* Form Actions */}
        <div className="form-actions">
          <button
            type="submit"
            className="btn btn-primary"
            disabled={isLoading}
          >
            {isLoading ? 'Saving...' : scenarioId ? 'Update Scenario' : 'Create Scenario'}
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={onCancel}
            disabled={isLoading}
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};
